import { Box, Button, Select, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { TENANT_CURRENCY_LABELS } from 'src/common/currency';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import {
  useSetTenantCurrency,
  useTenantConfig,
  useUpdateTenantConfig,
  type TenantConfig,
} from 'src/secret-menu/hooks/useTenantConfig';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';

export default function TenantSettingsTab() {
  return <CompanyWrapper contentComponent={TenantSettingsContent} />;
}

function TenantSettingsContent({ company }: { company: Company }) {
  const { config, isLoading, refetch } = useTenantConfig(company.id);
  const { updateConfig, isLoading: isUpdating } = useUpdateTenantConfig(company.id);
  const { setCurrency, isLoading: isUpdatingCurrency } = useSetTenantCurrency(company.id);

  const form = useForm({
    initialValues: {
      authMethod: 'password' as TenantConfig['authMethod'],
      currency: 'GBP' as TenantConfig['currency'],
    },
  });

  useEffect(() => {
    if (config) {
      form.initialize({ authMethod: config.authMethod, currency: config.currency });
    }
  }, [config]);

  if (isLoading) {
    return <PageLoader />;
  }

  const handleSubmit = async (values: typeof form.values) => {
    try {
      if (values.authMethod !== config?.authMethod) {
        await updateConfig({ authMethod: values.authMethod });
      }
      if (values.currency !== config?.currency) {
        await setCurrency(values.currency);
      }
      await refetch();
      form.resetDirty(values);
    } catch (error) {
      notifyAndLogError(
        'Failed to save settings',
        'An error occurred while saving settings.',
        error,
      );
    }
  };

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" maw={400}>
          <Text fw={600} size="lg">
            Tenant Settings for {company.name}
          </Text>

          <Select
            label="Authentication Method"
            description="Controls how users authenticate when accepting invites"
            data={[
              { value: 'password', label: 'Password' },
              { value: 'sso_only_microsoft', label: 'SSO Only (Microsoft)' },
              { value: 'sso_only_google', label: 'SSO Only (Google)' },
            ]}
            {...form.getInputProps('authMethod')}
          />

          <Select
            label="Currency"
            description="The currency all monetary values in this tenant are displayed in. Existing figures are not converted."
            data={Object.entries(TENANT_CURRENCY_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            {...form.getInputProps('currency')}
          />

          <Button
            type="submit"
            loading={isUpdating || isUpdatingCurrency}
            disabled={!form.isDirty()}
          >
            Save Changes
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
